<template>
    <div v-if="!isDefaultGroup || (isDefaultGroup && sortContracts && sortContracts.length > 0)">
        <div ref="group-container">
            <div class="flex gap-8 h-40">
                <div
                    :id="'edit-group-drag-' + localGroup.id" v-if="!isDefaultGroup"
                    class="draggable flex items-center text-primary-20 cursor-pointer trans-ease pointer-events-auto"
                >
                    <svg-icon type="drag-indicator" />
                </div>
                <div v-if="!isEditingGroupName" class="flex items-center justify-center">
                    <h2 class="text-body font-semibold">
                        {{ !localGroup.name || isDefaultGroup ? $t('multi-posting.contracts.groups.group.default.title.label') : localGroup.name }}
                    </h2>
                </div>
                <div v-else-if="isSavingGroupName" class="flex items-center ml-5">
                    <loading size="medium" align="left"/>
                </div>
                <div v-else class="flex items-center ml-5">
                    <simple-input
                        ref="edit-group-input"
                        :id="'edit-group-input-' + localGroup.id" v-model="editedGroupName"
                        :placeholder="$t('multi-posting.contracts.groups.group.edit.input.placeholder')"
                    />
                </div>
                <div class="flex items-center gap-4">
                    <button
                        @click="isOpen = !isOpen"
                    >
                        <svg-icon :type="isOpen ? 'keyboard-arrow-up' : 'keyboard-arrow-down'"/>
                    </button>
                    <button
                        :id="'edit-group-button-' + localGroup.id"
                        v-if="!isDefaultGroup"
                        @click="editGroupName"
                    >
                        <tooltip
                            :content="$t('multi-posting.contracts.groups.group.edit.tooltip')"
                            position="bottom"
                        >
                            <svg-icon type="edit"/>
                        </tooltip>
                    </button>
                    <button
                        class="text-semantic-error"
                        :id="'delete-group-button-' + localGroup.id"
                        v-if="!isDefaultGroup"
                        @click="startDeletingGroup"
                    >
                        <tooltip
                            :content="$t('multi-posting.contracts.groups.group.delete.tooltip')"
                            position="bottom"
                        >
                            <svg-icon type="delete"/>
                        </tooltip>
                    </button>
                </div>
            </div>
            <draggable
                v-if="isOpen"
                v-model="sortContracts"
                handle=".draggable"
                item-key="id"
                class="mb-32"
                @start="dragStart"
            >
                <template #item="{ element }">
                    <div class="bg-white rounded" :data-contract="element.contract_id">
                        <ContractListItem
                            :contract="element"
                            @contract-removed="contractRemoved"
                            @change-tab="$emit('change-tab', {tab: 'Campaigns', params: 'create'})"
                        />
                    </div>
                </template>
            </draggable>
        </div>
        <popup-modal v-if="isDeletingGroup" custom-classes="w-600" :text-center="false" @close="stopDeletingGroup">
            <div>
                <div class="flex mb-100" >
                    <div class="flex items-center mr-5">
                        <svg-icon class="w-20 h-20" type="delete"/>
                    </div>
                    <div class="flex items-center justify-center">
                        <span class="text-body font-semibold" >{{ $t('multi-posting.contracts.groups.group.popup.delete.header') }}</span>
                    </div>
                </div>
                <div class="flex items-center mt-16">
                    <p>{{ $t('multi-posting.contracts.groups.group.popup.delete.primary.text', { group : this.localGroup.name }) }}</p>
                </div>
            </div>
            <div>
                <div class="flex justify-end">
                    <simple-button
                        class="mt-24 mr-5"
                        display="primary"
                        look="primary"
                        @click="isDeletingGroup = false"
                    >
                      {{ $t('multi-posting.contracts.groups.group.popup.delete.secondary.button') }}
                    </simple-button>
                    <simple-button
                        class="mt-24"
                        display="danger"
                        look="primary"
                        :loading="isBusyDeletingGroup"
                        @click="deleteGroup"
                    >
                        {{ $t('multi-posting.contracts.groups.group.popup.delete.primary.button') }}
                    </simple-button>
                </div>
            </div>
        </popup-modal>
    </div>
</template>
<script>

import { mapState, mapActions } from "pinia";
import {useMainStore} from '../../state/store';
import {useMultiPostingStore} from '../../state/modules/multiPosting';
import {useNotificationsStore} from '../../state/modules/notifications';
import ContractListItem from "./ContractListItem.vue";
import SvgIcon from '../ui/SvgIcon.vue';
import Input from '../forms/Input.vue';
import SimpleButton from '../ui/SimpleButton.vue';
import Loading from '../ui/Loading.vue';
import { ADD_NOTIFICATION } from "../../state/mutation-types";
import Tooltip from '../navigation/Tooltip.vue';
import draggable from 'vuedraggable';
import PopupModal from "../../components/ui/PopupModal.vue";

// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

export default {
    name: 'ContractListGroup',
    components: {
        ContractListItem,
        'svg-icon': SvgIcon,
        'simple-input': Input,
        'loading': Loading,
        Tooltip,
        draggable,
        'simple-button': SimpleButton,
        PopupModal
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'currentUser']),
        sortContracts: {
            get() {
                const filtered = this.localContracts.filter((contract) => {
                    // regex to check if the group id is a valid UUID, only used to add contracts to the default group when there were created before the groups feature was added.
                    const regex = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
                    return (this.isDefaultGroup && (!contract.group?.id || regex.test(contract.group?.name))) || contract.group?.id === this.group.id;
                });

                if (this.localGroup.contracts) {
                    for(let i = 0; i < this.localGroup.contracts.length; i++) {
                        const contract = this.localGroup.contracts[i];
                        if (contract) {
                            const found = filtered.find((item) => item.contract_id === contract.id);
                            if (found) {
                                found.position = contract.position;
                            }
                        }
                    }
                }

                const groupsWithIndex = filtered.filter(contract => contract.position !== undefined);
                const groupsWithoutIndex = filtered.filter(contract => contract.position === undefined);
                const sortedGroups = groupsWithIndex.sort((a, b) => a.position - b.position);

                const result = [...sortedGroups, ...groupsWithoutIndex.sort((a, b) => a.name.localeCompare(b.name))];
                return result;
            },
            set(value) {
                this.previousContracts = _.cloneDeep(this.localContracts);

                value.forEach((item, index) => {
                   item.position = index;
                });

                this.localGroup.contracts = value.map(item => ({ id: item.contract_id, position: item.position }));

                // Temporaly update the position of local contracts, so the screen displays the correct result.
                this.localContracts = this.localContracts.map((contract) => {
                    const found = value.find((item) => item.id === contract.contract_id);
                    if (found) {
                        contract.position = found.position;
                    }
                    return contract;
                });

                this.moveContractGroups(
                  {
                    groups: [this.localGroup]
                  }
                ).then(() => {
                    const contract = this.contractName;
                    const groupName = this.localGroup.name;
                    this[ADD_NOTIFICATION](
                        {
                            type: 'success',
                            message: this.$t('multi-posting.contracts.groups.group.sort.contract.succcess', { contract, group: groupName }),
                        }
                    );
                }
                ).catch(() => {
                    this.localContracts = this.previousContracts;
                    const contract = this.contractName;
                    const groupName = this.localGroup.name;
                    this[ADD_NOTIFICATION](
                        {
                            type: 'error',
                            message: this.$t('multi-posting.contracts.groups.group.sort.contract.error', { contract, group: groupName }),
                        }
                    );
                });
            }
        },
        contractName() {
            let contractName = this.lastItemDragged.name;
            if (this.lastItemDragged.alias) {
                contractName = contractName + ` - (${this.lastItemDragged.alias})`;
            }
            return contractName;
        },
        isDefaultGroup() {
            return this.localGroup.name === 'Default';
        },
        deleteGroupLabel() {
            return this.$t('multi-posting.contracts.groups.group.delete.label', { group: this.localGroup.name });
        },
    },
    data: function () {
        return {
            localContracts: _.cloneDeep(this.contracts), // Create a deep copy of the contracts prop
            localGroup: _.cloneDeep(this.group),
            isEditingGroupName: false,
            isSavingGroupName: false,
            editedGroupName: '',
            previousContracts: [],
            lastItemDragged: null,
            isOpen: true,
            isDeletingGroup: false,
            isBusyDeletingGroup: false
        }
    },
    props: {
        contracts: {
            type: Array,
            default: () => []
        },
        title: {
            type: String,
            default: ''
        },
        group: {
            type: Object,
            default: () => ({})
        },
        filter: {
            type: Function,
            default: () => true
        }
    },
    methods: {
        ...mapActions(useMultiPostingStore, ['editContractGroup', 'moveContractGroups', 'deleteContractGroup']),
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        contractRemoved(id) {
            this.$emit('contract-removed', id);
        },
        editGroupName() {
            if (this.isEditingGroupName) {
                this.saveGroupName();
                return;
            }

            this.isEditingGroupName = true;
            this.editedGroupName = this.localGroup.name;
            this.$nextTick(() => {
                const inputElement = this.$refs['edit-group-input'];
                if (inputElement) {
                    inputElement.focus();
                }
            });
        },
        saveGroupName() {
            if (!this.editedGroupName || String(this.editContractGroup).trim().length === 0 || String(this.editedGroupName) === String(this.localGroup.name)) {
                this.isEditingGroupName = false;
                return;
            }

            this.isSavingGroupName = true;
            this.editContractGroup({ groupId: this.localGroup.id, groupName: this.editedGroupName })
                .then(() => {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('multi-posting.contracts.groups.group.edit.success'),
                    });
                })
                .catch(() => {
                    this[ADD_NOTIFICATION]({
                        type: 'error',
                        message: this.$t('multi-posting.contracts.groups.group.edit.error'),
                    });
                })
                .finally(() => {
                    this.editedGroupName = '';
                    this.isEditingGroupName = false;
                    this.isSavingGroupName = false;
                });
        },
        handleEnterKey(event) {
            if (event.key === 'Enter' && this.isEditingGroupName) {
                this.saveGroupName();
            }
        },
        dragStart(event) {
            this.lastItemDragged = this.contracts.find((contract) => contract.contract_id === event.item.getAttribute('data-contract'));
        },
        startDeletingGroup() {
            this.isDeletingGroup = true;
        },
        stopDeletingGroup() {
            this.isDeletingGroup = false;
        },
        deleteGroup() {
            this.isBusyDeletingGroup = true;
            this.deleteContractGroup({
                groupId: this.localGroup.id
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('multi-posting.contracts.groups.group.delete.success', { group: this.localGroup.name}),
                });
            }).catch(() => {
                this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: this.$t('multi-posting.contracts.groups.group.delete.error', { group: this.localGroup.name}),
                });
            }).finally(() => {
                this.isBusyDeletingGroup = false;
                this.isDeletingGroup = false;
            });
        }
    },
    watch: {
        contracts: {
            handler(newContracts) {
                this.localContracts = _.cloneDeep(newContracts);
            },
            deep: true
        },
        group: {
            handler(newGroup) {
                this.localGroup = _.cloneDeep(newGroup);
            },
            deep: true
        }
    },
    mounted() {
        document.addEventListener('keydown', this.handleEnterKey);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleEnterKey);
    }

}
</script>
